<script setup>
import { useAbility } from '@casl/vue';
import { Smartphone } from 'lucide-vue-next';

import AuthenticationButtons from '@/components/authentication/AuthenticationButtons.vue';
import EnvOverrideChip from '@/components/dev/EnvOverrideChip.vue';
import ClockMarketplace from '@/components/header/ClockMarketplace.vue';
import HeaderAccountInfo from '@/components/header/HeaderAccountInfo.vue';
import HeaderAiActionsBadge from '@/components/header/HeaderAiActionsBadge.vue';
import HeaderGuidedTour from '@/components/header/HeaderGuidedTour.vue';
import HeaderLanguageSelector from '@/components/header/HeaderLanguageSelector.vue';
import HeaderMenu from '@/components/header/HeaderMenu';
import HeaderSupportChat from '@/components/header/HeaderSupportChat.vue';
import HeaderUserPopup from '@/components/header/HeaderUserPopup.vue';
import { NotificationBell, NotificationDrawer } from '@/components/notifications';
import IconUser from '@/components/ui/icons/User.vue';
import Switcher from '@/components/ui/inputs/Switcher.vue';
import SwitcherBlur from '@/components/ui/inputs/SwitcherBlur.vue';
import Logo from '@/components/ui/logo/Logo';
import UiBreadcrumbs from '@/components/ui/UiBreadcrumbs.vue';
import { useDevOnlyOverride } from '@/composables/useDevOnlyOverride';
import { useIsTouchDevice } from '@/composables/useIsTouchDevice';
import { useVisibility } from '@/composables/useVisibility';
import { maskEmail } from '@/helpers/helpers.js';
import { getOverviewRoute } from '@/router/utils';
import { useAccountStore } from '@/stores/account.js';
import { useAuthStore } from '@/stores/auth';
import { useBlurStore } from '@/stores/blur';
import { useEnvironmentStore } from '@/stores/environment';
import { useThemeStore } from '@/stores/theme';
import { useViewportStore } from '@/stores/viewport';

const { userTheme, LIGHT_THEME, toggleTheme } = useThemeStore();
const { userBlur, toggleBlur } = useBlurStore();
const { isDevelopment } = useEnvironmentStore();
const viewportStore = useViewportStore();
const secondaryMenuOpen = ref(false);
const closeSecondaryMenu = () => { secondaryMenuOpen.value = false; };
const { isTouchDevice } = useIsTouchDevice();
const authStore = useAuthStore();
const { loggedIn } = storeToRefs(useAuthStore());
const ability = useAbility();
// Same gate as the AI Actions sidebar item (read / Management): Owner and Admin.
const canManage = computed(() => ability.can('read', 'Management'));
const { unifiedMutationsActive } = useDevOnlyOverride();
const { visible, toggleVisibility, hide } = useVisibility();

const themes = [
  {
    name: 'light',
  },
  {
    name: 'dark',
  },
];

const user = computed(() => authStore.user);

const accountStore = useAccountStore();
// The logo opens the Overview, which spans every connected marketplace.
const logoTarget = computed(() => getOverviewRoute(accountStore));

onBeforeMount(async () => {
  await authStore.getUser();
});
</script>

<template>
  <header class="header layout-default-header">
    <RouterLink
      class="navigation-link"
      :to="logoTarget"
      :aria-label="$t('Overview')"
    >
      <Logo class="logo" />
    </RouterLink>

    <UiBreadcrumbs />

    <ClockMarketplace class="header-marketplace-clock" />
    <div class="header-menu">
      <HeaderSupportChat class="header-support-chat" />

      <div class="header-options laptop-only">
        <EnvOverrideChip />

        <HeaderGuidedTour />

        <div v-click-outside="closeSecondaryMenu" class="header-secondary" @keydown.esc="closeSecondaryMenu">
          <button
            class="header-secondary-toggle"
            type="button"
            aria-label="Appearance and language"
            title="Appearance and language"
            :aria-expanded="secondaryMenuOpen"
            @click="secondaryMenuOpen = !secondaryMenuOpen"
          >
            •••
          </button>
          <div class="header-secondary-controls" :class="{ 'is-open': secondaryMenuOpen }">
            <SwitcherBlur
              :blur="userBlur"
              class="header-blur-switcher"
              @change-blur="toggleBlur"
            />

            <Switcher
              :default-value="userTheme === LIGHT_THEME"
              :themes="themes"
              class="header-theme-switcher"
              @change-theme="toggleTheme"
            />
            <HeaderLanguageSelector
              v-if="isDevelopment"
              class="header-language-selector"
            />
          </div>
        </div>
        <!-- Laptop cluster only. The phone header has no agent badge. -->
        <HeaderAiActionsBadge v-if="loggedIn && canManage && unifiedMutationsActive" />
        <NotificationBell v-if="loggedIn && isDevelopment" />
        <NotificationDrawer v-if="isDevelopment" />
        <template v-if="loggedIn">
          <HeaderAccountInfo />
          <div class="header-user-wrap">
            <button
              v-click-outside="hide"
              :class="{ 'header-user--active': visible }"
              class="header-user"
              @click="toggleVisibility"
            >
              <div class="header-account-icon">
                <IconUser />
              </div>
              <HeaderUserPopup
                v-if="visible"
                class="header-user-popup"
                :user="user"
                @close="visible = false"
              />
              <span
                v-if="user && user['custom:firstName'] && user['custom:lastName']"
                v-no-translate
                class="header-user-name"
              >{{ user['custom:firstName'] }} {{ user['custom:lastName'] }}</span>
              <span
                v-else-if="user && user.email"
                v-no-translate
                class="header-user-name"
              >{{ maskEmail(user?.email) }}</span>
            </button>
          </div>
        </template>

        <AuthenticationButtons
          v-else
          class="header-authentication"
          login-button-text="Log In"
          register-button-text="Sign Up"
        />
      </div>

      <div class="header-burger-button">
        <HeaderMenu />
      </div>
    </div>

    <button
      v-if="isTouchDevice && viewportStore.isDesktopViewport"
      class="header-viewport-toggle"
      :title="$t('Switch to mobile view')"
      @click="viewportStore.setPreference('mobile')"
    >
      <Smartphone :size="26" />
    </button>
  </header>
</template>

<style lang="scss" scoped>
.header {
  background-color: var(--wp-surface-header);

  &-menu {
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }

  &-support-chat {
    margin-right: 8px;

    @include laptop {
      margin-right: var(--wp-density-gap, 18px);
    }
  }

  &-wallet {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    position: relative;
    min-height: auto;
    margin-right: 15px;

    &::after {
      content: '';
      display: block;
      position: absolute;
      top: 0;
      right: -17px;
      bottom: 0;
      width: 1px;
      background-color: var(--borders);
    }

    & svg path {
      fill: var(--accent);
    }
  }

  &-balance {
    display: inline-block;
    margin-left: 8px;
    font-size: 0.875rem;
    line-height: 1.2rem;
  }

  &-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px;
    @include laptop {
      padding: 16px;
    }
  }

  &-market-wrapper {
    border-top: 1px solid var(--borders);
    border-bottom: 1px solid var(--borders);
  }

  &-market-info {
    margin: 0 auto;
    width: 100%;
    max-width: 1240px;
    padding: 10px;
    @include laptop {
      padding: 16px;
    }
  }

  &-chat {
    position: relative;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    background-color: var(--default-text-field);

    &.phone-chat {
      margin-left: 16px;
    }

    span {
      display: flex;
      position: absolute;
      top: 0;
      right: 0;
      justify-content: center;
      align-items: center;
      border: 1px solid var(--bg);
      border-radius: 50%;
      width: 20px;
      height: 20px;
      transform: translate(20%, -20%);
      background-color: var(--accent);
      font-size: 0.625rem;
      font-weight: 700;
      color: var(--bg);
    }
  }
}

.header-viewport-toggle {
  display: flex;
  position: fixed;
  z-index: 100;
  top: 68px;
  right: 16px;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border: 1px solid var(--wp-border-primary);
  border-radius: 50%;
  background-color: var(--wp-surface-card);
  color: var(--wp-icon-accent);
  box-shadow: 0 4px 12px var(--wp-shadow-card);
  cursor: pointer;
}

@media (max-width: 479px) {
  .header .navigation-link {
    :deep(.logo) {
      width: 36px;
    }

    :deep(.desktop-logo) {
      display: none;
    }

    :deep(.mobile-logo) {
      display: block;
      width: 36px;
      height: 36px;
    }
  }
}

.navigation {
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 450px;

  &-list {
    display: none;
  }

  &-link {
    display: flex;
    align-items: center;
    transition: color var(--transition-fast);
    line-height: 1.5rem;
    white-space: nowrap;
    color: var(--primary-text);
    gap: var(--gap-small);

    &:hover {
      color: var(--accent);
    }

    .icon {
      min-width: 24px;
      width: 24px;
      height: 24px;

      &:deep(path) {
        transition: fill var(--transition-fast);
      }
    }
  }
}

.pnl-mobile {
  display: none;
}

.pnl-desktop {
  display: flex;
  text-align: right;
  min-width: 100px;
}

.settings__select {
  &:deep(.select__main-btn) {
    background: var(--sub-bg);
    display: flex;
    align-content: center;
    justify-content: center;
    padding: 4px;
    border-radius: 18px;
    transition: none;
  }
}

.header {
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: space-between;
  padding: 12px 10px;
}

@include laptop {
  .header {
    display: flex;
    align-items: center;
    width: 100%;
    justify-content: space-between;
    padding: 12px 32px;

    &.layout-default-header {
      & .toggle-switch {
        height: 36px;
        svg {
          width: 22px;
          height: 22px;
        }
      }

      & .header-user-wrap {
        gap: 8px;
        display: flex;
        align-items: center;
        justify-content: flex-start;
        background: var(--sub-bg);
        border-radius: 20px;
        padding: 4px;

        @include desktop {
          padding: 6px 10px 6px 6px;
        }
      }

      & .header-user {
        height: auto;
        width: auto;
        font-size: 14px;
        font-style: normal;
        font-weight: 500;
        line-height: 16px;
      }

      .header-account-icon {
        padding: 4px;
        background: var(--wp-surface-bg-icon);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        width: auto;
        height: auto;

        &:deep(path) {
          fill: var(--text-main-color);
        }

        @include desktop {
          margin-right: 8px;
        }

        svg {
          width: 20px;
          height: 20px;
        }
      }

      .header-user-name {
        color: var(--wp-text-accent);
        display: none;
      }

      @include desktop {
        .header-user-name {
          display: block;
        }
      }

      &:hover {
        .header-user-name {
          color: var(--wp-text-accent);
        }
      }

      & .settings__select .select__main-btn {
        height: 36px;
        width: 36px;
      }
    }

    &-wallet {
      margin-right: 0;
      min-height: 40px;

      &.phone-only {
        display: none;
      }
    }

    &-user {
      display: flex;
      position: relative;
      justify-content: center;
      align-items: center;
      border-radius: 50%;
      width: 36px;
      height: 36px;



      &-popup {
        position: absolute;
        right: -9px;
        top: calc(100% + 8px);
        background: var(--wp-surface-popup);
        border: 1px solid var(--wp-border-primary) !important;
        box-shadow: var(--popover-shadow);
      }
    }

    &-theme-switcher {
      display: none;
    }

    &-options {
      @include laptop {
        display: flex;
        align-items: center;
        gap: 18px;
      }
    }

    &-authentication {
      display: flex;
      flex-direction: row-reverse;
      align-items: center;
      padding: 0;
      gap: 16px;

      &:deep(button) {
        margin: 0 !important;
        padding: 8px 24px;
        max-height: 32px;
        font-size: 14px;
        font-style: normal;
        font-weight: 500;
        line-height: 16px;
      }
    }
  }

  .navigation {
    gap: 40px;

    &-link {
      font-size: 1rem;
    }
  }

  .pnl-mobile {
    display: flex;
    margin-right: 30px;
    flex-shrink: 0;
  }
}

.pnl-percentage {
  display: flex;
  align-items: center;

  .direction {
    width: 19px;
    height: 19px;
  }
}

.notification {
  cursor: pointer;
}

@include laptop {
  .header-theme-switcher {
    display: block;
    position: relative;
  }

  .header-burger-button {
    display: none;
  }

  .pnl-mobile {
    display: none;
  }

  .pnl-desktop {
    display: flex;
    text-align: right;
    min-width: 100px;
  }

  .navigation {
    &-list {
      display: flex;
      align-items: center;
      gap: 25px;
    }

    &-link {
      font-size: 1.25rem;
      padding-right: 16px;
    }
  }
}

:deep(.header-icon) {
  background: var(--sub-bg);
  width: 26px;
  height: 26px;
  display: inline-block;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  vertical-align: top;
  margin-top: -1px;

  & img {
    width: 24px;
    height: 24px;
    object-fit: contain;
  }
}

:deep(.header-account) {
  position: relative;
  cursor: pointer;
}

:deep(.header-dropdown) {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  border-radius: 10px;
  background: var(--wp-surface-popup);
  border: 1px solid var(--wp-border-primary) !important;
  box-shadow: var(--popover-shadow);
  padding: 16px;
  width: 210px;
  z-index: 99;

  & {
    .p-accordionpanel {
      border: none;
      cursor: default;

      .p-accordionheader {
        background: var(--wp-surface-card);
        padding: 8px 16px;
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 20px;
        color: var(--wp-text-primary);
        justify-content: space-between;
        width: 100%;
        box-sizing: border-box;

        &:disabled {
          background: var(--wp-surface-input);
        }

        &:not(.p-accordionpanel-active):not(.p-disabled) > &:hover {
          background: var(--wp-surface-input);
        }

        &:hover {
          background: var(--wp-surface-input);
        }
      }
    }

    .p-accordionpanel:not(.p-accordionpanel-active):not(.p-disabled) > .p-accordionheader:hover {
      background: var(--wp-surface-input);
      color: var(--wp-text-accent);

      &:deep(.p-accordionheader-toggle-icon) {
        transition: opacity 0.5s;
        opacity: 1;
      }
    }

    &:deep(.p-accordionheader-toggle-icon) {
      opacity: 1;

      path {
        fill: var(--wp-text-tertiary);
      }
    }

    &:deep(.p-accordionheader-toggle-icon) {
      opacity: 1;
      transform: rotate(180deg);
      margin-left: auto;

      path {
        fill: var(--wp-text-tertiary);
      }
    }

    &:deep(
      .p-accordionpanel:not(.p-disabled).p-accordionpanel-active
        > .p-accordionheader:hover
        .p-accordionheader-toggle-icon
    ) {
      opacity: 1;
    }

    .p-accordionpanel:not(.p-disabled).p-accordionpanel-active
      > .p-accordionheader
      .p-accordionheader-toggle-icon {
      color: var(--wp-text-tertiary);
      width: 14px;
      height: 14px;
    }

    .p-accordionpanel:not(.p-disabled).p-accordionpanel-active > .p-accordionheader {
      background: var(--wp-surface-input);
      color: var(--wp-text-accent);

      .business-profile-name {
        color: var(--wp-text-accent);
      }
    }

    .p-accordionpanel:not(.p-accordionpanel-active):not(.p-disabled)
      .p-accordionheader:hover
      .p-accordionheader-toggle-icon {
      color: var(--wp-text-tertiary);
    }

    &:deep(.p-accordioncontent) {
      display: none;
      overflow: visible !important;
    }

    &:deep(.p-accordionpanel.p-accordionpanel-active .p-accordioncontent) {
      display: block;
    }

    &:deep(.p-accordionpanel.p-accordionpanel-active .p-accordionheader-toggle-icon) {
      opacity: 1;
      transform: rotate(180deg);
    }

    &:deep(.p-toggleable-content-enter-active) {
      transition: none;
    }
  }
}

:deep(.header-dropdown-account) {
  padding: 0;
  cursor: default;
}

:deep(.p-accordioncontent-content) {
  background: var(--wp-surface-card);
  padding: 6px 16px;
  border-radius: 8px;
  box-shadow: none;
  margin-top: 0px;
  margin-bottom: 0px;
  color: var(--wp-text-primary);
}

:deep(.header-dropdown-content) {
  max-height: 195px;
  overflow: auto;
  overscroll-behavior: none;
  padding: 4px 0;
}

.breadcrumb {
  display: none;
  margin-left: 20px;
  margin-right: auto;

  @include large-desktop {
    display: flex;
  }

  &-before-link {
    color: var(--wp-text-muted);
    font-size: 16px;
    line-height: 24px;
  }

  &-current-link {
    color: var(--wp-text-primary);
    font-size: 16px;
    line-height: 24px;
  }
}
//
//.navigation-link {
//  display: none;
//}

.header-marketplace-clock {
  margin-right: 16px;
}
</style>

<style lang="scss">
.header-secondary { position: relative; }
.header-secondary-controls { display: flex; align-items: center; gap: 18px; }
.header-secondary-toggle { display: none; }
@media (min-width: 769px) and (max-width: 1440px) {
  .header-secondary-controls { gap: var(--wp-density-gap); }
}
@media (min-width: 769px) and (max-width: 1600px) {
  .header-secondary-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 8px;
    color: var(--wp-text-primary);
    background: var(--wp-surface-white);
    cursor: pointer;
  }
  .header-secondary-controls {
    display: none;
    position: absolute;
    inset-inline-end: 0;
    top: calc(100% + 8px);
    padding: 12px;
    border: 1px solid var(--wp-border-primary);
    border-radius: 8px;
    background: var(--wp-surface-popup);
    z-index: 100;
    &.is-open { display: flex; }
  }

  .v-theme--light .header-secondary-controls {
    background: var(--wp-surface-secondary);
  }
}
</style>
